import { IInfluencer, InfluencerContact } from "@shared/lib/types/influencer";

import { DeleteOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { useAddContact } from "@entities/influencer/lib/hooks/useAddContact";
import { useRemoveContact } from "@entities/influencer/lib/hooks/useRemoveContact";
import { Button } from "@shared/ui/Button";
import { Col, Divider, Modal, Row, Space, Switch, Tag } from "antd";
import styles from "../styles.module.scss";

import { useUpdateContact } from "@entities/influencer/lib/hooks/useUpdateContact";
import { ToastService } from "@shared/lib/services";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { useEffect, useState } from "react";
import ContactForm from "./ContactForm";

interface Props {
  contacts: InfluencerContact[];
  influencerId: string;
  linkedAgencies: IInfluencer["linkedAgencies"];
}
export function ContactsList({
  contacts,
  influencerId,
  linkedAgencies,
}: Props) {
  const { isRemoved, isRemoving, removeContact } = useRemoveContact();
  const { addContact, isAdded, isAdding } = useAddContact();

  const [isVerifying, setisVerifying] = useState<boolean>(false);
  const { updateContact, isUpdated, isUpdating, variables } =
    useUpdateContact();
  const [deletedReviewId, setdeletedReviewId] = useState<string>("");
  const [reviewModalProps, setReviewModalVisible] = useState<{
    visble: boolean;
    selectedContact?: InfluencerContact;
  }>({ visble: false, selectedContact: undefined });

  const handleDelete = (contact: InfluencerContact) => {
    setdeletedReviewId(contact.influencerContactId);
    removeContact({
      influencerId,
      contact: {
        ...contact,
        agency: contact.agency?._id ? contact.agency?._id : undefined,
      },
    });
  };
  const handleContact = (values: Partial<InfluencerContact>) => {
    if (reviewModalProps.selectedContact) {
      return updateContact({
        influencerId,
        contact: {
          ...values,
          influencerContactId:
            reviewModalProps.selectedContact.influencerContactId,
        },
      });
    }
    addContact({
      influencerId,
      contact: { ...values, influencerContactId: "" },
    });
  };

  const handleMakePrimary = (contact: InfluencerContact) => {
    updateContact({
      influencerId,
      contact: {
        ...contact,
        isPrimaryContact: true,
        // @ts-ignore
        agency: contact.agency ? contact.agency?._id : undefined,
      },
    });
  };
  const handleToggleVerify = (contact: InfluencerContact) => {
    setisVerifying(true);
    updateContact({
      influencerId,
      contact: {
        ...contact,

        // @ts-ignore
        agency: contact.agency ? contact.agency?._id : undefined,
        isVerified:
          contact?.isPrimaryContact === undefined ? true : !contact.isVerified,
      },
    });
  };

  useEffect(() => {
    if (isAdded || isUpdated) {
      ToastService.success("Contacts Upated");
      setReviewModalVisible({ visble: false, selectedContact: undefined });
      setisVerifying(false);
    }
  }, [isAdded, isUpdated]);
  useEffect(() => {
    if (isRemoved) {
      ToastService.success("Contact Deleted");
      setdeletedReviewId("");
      setReviewModalVisible({ visble: false, selectedContact: undefined });
    }
  }, [isRemoved]);

  return (
    <div className={styles.card}>
      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          alignItems: "start",
          marginBottom: "12px",
        }}
      >
        <div className={styles.permTitle}>Contacts</div>
        <Button
          variant="outlined"
          size="middle"
          onClick={() =>
            setReviewModalVisible({ visble: true, selectedContact: undefined })
          }
          icon={<PlusOutlined />}
          iconPosition="end"
        >
          Add Contact
        </Button>
      </Space>

      <Divider style={{ margin: 0, marginBottom: 16 }} />
      {contacts.length ? (
        <div className={styles.permContent}>
          {contacts.map((permItem) => (
            <>
              <PermTile
                contact={permItem}
                handleDelete={handleDelete}
                handleSelect={(contact) =>
                  setReviewModalVisible({
                    visble: true,
                    selectedContact: contact,
                  })
                }
                handleToggleVerify={handleToggleVerify}
                isLoading={isRemoving}
                selectedId={deletedReviewId}
                selectedPrimaryId={
                  variables?.contact?.influencerContactId || ""
                }
                handleMakePrimary={handleMakePrimary}
                isMakingPrimary={isUpdating}
                isVerifying={isVerifying}
              />
              <Divider style={{ margin: 0 }} />
            </>
          ))}
        </div>
      ) : (
        <div>No Contacts found</div>
      )}
      <Modal
        title={
          reviewModalProps.selectedContact ? "Update Contact" : "Add Contact"
        }
        open={reviewModalProps.visble}
        onCancel={() =>
          setReviewModalVisible({ selectedContact: undefined, visble: false })
        }
        destroyOnClose
        footer={null}
      >
        <ContactForm
          isSubmitting={isAdding || isUpdating}
          onSubmit={handleContact}
          initialValues={reviewModalProps.selectedContact}
          linkedAgencies={linkedAgencies}
        />
      </Modal>
    </div>
  );
}

export function PermTile({
  contact,
  handleDelete,
  handleSelect,
  isLoading,
  selectedId,
  selectedPrimaryId,
  handleMakePrimary,
  isMakingPrimary,
  handleToggleVerify,
  isVerifying,
}: {
  contact: InfluencerContact;
  handleDelete: (contact: InfluencerContact) => void;
  handleSelect: (contact: InfluencerContact) => void;
  selectedId: string;
  isLoading: boolean;
  selectedPrimaryId: string;
  handleMakePrimary: (contact: InfluencerContact) => void;
  isMakingPrimary: boolean;
  handleToggleVerify?: (contact: InfluencerContact) => void;
  isVerifying?: boolean;
}) {
  return (
    <Row style={{ width: "100%" }} justify={"space-between"} align="top">
      <Col span={6}>
        <Space align="start" size={2}>
          <div>
            <div className={styles.permItemTitle}>
              <TextWithTooltip
                text={
                  contact?.agencyUser
                    ? `${contact.agencyUser.name} (${contact.agencyUser.role})`
                    : contact.name
                }
                limit={20}
              />
              {}
            </div>
            <div className={styles.permItemDesc}>{contact.type}</div>
          </div>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={() => handleSelect(contact)}
          />
        </Space>
      </Col>
      <Col span={6}>
        <div className={styles.permItemDesc}>
          {contact?.agencyUser ? (
            <>
              {contact.agencyUser?.countryCode}{" "}
              {contact.agencyUser?.phoneNumber}
            </>
          ) : (
            <>
              {contact.countryCode} {contact.phoneNumber}
            </>
          )}
        </div>
      </Col>
      <Col span={6}>
        <div className={styles.permItemDesc}>
          {contact.agencyUser ? (
            <>
              <br />
              {contact.agencyUser?.emailId}
            </>
          ) : (
            <>{contact.emailId}</>
          )}
        </div>
      </Col>
      <Col span={4}>
        <Space wrap>
          {contact.isPrimaryContact ? (
            <Tag color="green">Primary</Tag>
          ) : (
            <Button
              size="small"
              type="primary"
              onClick={() => handleMakePrimary(contact)}
              loading={
                isMakingPrimary &&
                !isVerifying &&
                selectedPrimaryId === contact.influencerContactId
              }
            >
              Make Primary
            </Button>
          )}
          {contact.agency && (
            <Tag color="purple">Agency: {contact.agency.name}</Tag>
          )}
          <Switch
            checkedChildren="Verified"
            unCheckedChildren="UnVerified"
            loading={
              isVerifying && selectedPrimaryId === contact.influencerContactId
            }
            onChange={() => handleToggleVerify?.(contact)}
            checked={
              contact.isVerified === undefined ? false : contact.isVerified
            }
          />
        </Space>
      </Col>

      <Col
        span={2}
        style={{
          justifyContent: "flex-end",
          display: "flex",
          height: "max-content",
        }}
      >
        <Button
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(contact)}
          loading={selectedId === contact.influencerContactId && isLoading}
        ></Button>
      </Col>
    </Row>
  );
}
